import { useState, type ReactNode } from 'react';
import type { z } from 'zod';

export function PageHeader({ title, intro, actions }: { title: string; intro?: string; actions?: ReactNode }) {
  return (
    <header className="page-header">
      <div>
        <h1>{title}</h1>
        {intro && <p className="muted">{intro}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error?: string | undefined;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className={`field${error ? ' field-invalid' : ''}`}>
      <span className="field-label">
        {label}
        {hint && <span className="muted"> ({hint})</span>}
      </span>
      {children}
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

export function Alert({ children, tone = 'error' }: { children: ReactNode; tone?: 'error' | 'info' }) {
  return (
    <div className={`alert alert-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      {children}
    </div>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="empty">{children}</p>;
}

export function Loading({ label }: { label: string }) {
  return (
    <p className="loading" aria-busy="true">
      {label}
    </p>
  );
}

/** A collapsible panel holding a creation form, opened by a button. */
export function Disclosure({ label, children }: { label: string; children: (close: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  if (!open) {
    return (
      <button type="button" className="button" onClick={() => setOpen(true)}>
        {label}
      </button>
    );
  }
  return (
    <section className="panel">
      <h2>{label}</h2>
      {children(() => setOpen(false))}
    </section>
  );
}

export type FieldErrors = Record<string, string>;

/**
 * Validates form values with a shared contract schema. Empty strings count as "not provided",
 * so optional fields can be left blank.
 */
export function validate<S extends z.ZodType>(
  schema: S,
  values: Record<string, unknown>,
): { data: z.output<S>; errors: null } | { data: null; errors: FieldErrors } {
  const cleaned = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== '' && v !== undefined));
  const result = schema.safeParse(cleaned);
  if (result.success) return { data: result.data, errors: null };
  const errors: FieldErrors = {};
  for (const issue of result.error.issues) {
    const key = issue.path.join('.') || '_form';
    errors[key] ??= issue.message;
  }
  return { data: null, errors };
}

export const toNumber = (v: string): number | undefined => (v.trim() === '' ? undefined : Number(v.replace(',', '.')));
